import { diffChars, diffWordsWithSpace, diffLines } from "diff";
import React from "react";
import {
  ActivityHistoryItem,
  ActivityHistoryState,
  ActivityHistoryValue,
  ValueRow,
} from "./types";
import { LinkValue } from "./types";
import { Assets } from "../../../../types/types.PIM";
import { DiffText } from "./styles";

export const EMPTY_VALUE = "-";
export const BUYER_CUSTOM_SKU_ATTRIBUTE_NAME =
  "Buyer can request a custom SKU when creating a quote for this product";

export function isNullOrUndefined<T>(
  val: T | null | undefined
): val is null | undefined {
  return val === null || val === undefined;
}

export function isNonNullObject<T>(
  val: T | null | undefined
): val is T & object {
  return typeof val === "object" && val !== null;
}

export function isPrimitive(val: unknown): val is string | number | boolean {
  return (
    typeof val === "string" ||
    typeof val === "number" ||
    typeof val === "boolean"
  );
}

export function isString(val: unknown): val is string {
  return typeof val === "string";
}

export function isObject(val: unknown): val is Record<string, unknown> {
  return typeof val === "object" && val !== null && !Array.isArray(val);
}

export function hasProperty<K extends string>(
  obj: unknown,
  prop: K
): obj is Record<K, unknown> {
  return isObject(obj) && prop in obj;
}

export function isActivityHistoryValue(
  val: unknown
): val is ActivityHistoryValue {
  return (
    isNonNullObject(val) &&
    hasProperty(val, "attribute_id") &&
    typeof val.attribute_id === "string"
  );
}

export function hasValueProperty(val: unknown): val is { value: unknown } {
  return isNonNullObject(val) && hasProperty(val, "value");
}

export function isActivityHistoryValueArray(
  arr: unknown[]
): arr is ActivityHistoryValue[] {
  return arr.length > 0 && isActivityHistoryValue(arr[0]);
}

export function getFirstElement<T>(
  arr: unknown[],
  guard: (val: unknown) => val is T
): T | null {
  return arr.length > 0 && guard(arr[0]) ? arr[0] : null;
}

export interface DiffPart {
  value: string;
  added?: boolean;
  removed?: boolean;
}

interface VersionGroup {
  version_number: number;
  status: string;
  items: ActivityHistoryItem[];
  latestModifiedAt: string;
  latestModifiedBy: string;
}

export function computeSmartDiff(before: string, after: string): DiffPart[] {
  if (before.includes("\n") || after.includes("\n")) {
    return diffLines(before, after);
  }

  if (before.length < 50 && after.length < 50) {
    return diffChars(before, after);
  }

  return diffWordsWithSpace(before, after);
}

export function stripHtmlTags(html: string): string {
  return html.replace(/<[^>]*>/g, "");
}

export function formatValueForDisplay(value: string | number | null): string {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value);
}

function isStateEmpty(state: ActivityHistoryState | null | undefined): boolean {
  if (!state || typeof state !== "object") return true;

  const hasValues =
    state.values && Array.isArray(state.values) && state.values.length > 0;
  const hasValueRow =
    state.value_row &&
    state.value_row.values &&
    state.value_row.values.length > 0;
  const hasValueRows =
    state.value_rows &&
    Array.isArray(state.value_rows) &&
    state.value_rows.length > 0;
  const hasStatus = state.status != null && state.status !== undefined;

  const otherKeys = Object.keys(state).filter(
    (key) =>
      !["attributes", "values", "value_row", "value_rows", "status"].includes(
        key
      )
  );

  return (
    !hasValues &&
    !hasValueRow &&
    !hasValueRows &&
    !hasStatus &&
    !(otherKeys.length > 0)
  );
}

export function hasEmptyStates(item: ActivityHistoryItem): boolean {
  return isStateEmpty(item.state_before) && isStateEmpty(item.state_after);
}

export function groupActivitiesByVersion(
  items: ActivityHistoryItem[]
): VersionGroup[] {
  const versionGroups = new Map<number, ActivityHistoryItem[]>();

  items.forEach((item) => {
    const version = item.version_number;
    if (!versionGroups.has(version)) {
      versionGroups.set(version, []);
    }
    versionGroups.get(version)!.push(item);
  });

  const result: VersionGroup[] = [];
  Array.from(versionGroups.keys()).forEach((versionNumber) => {
    const versionItems = versionGroups.get(versionNumber)!;

    versionItems.sort(
      (a, b) =>
        new Date(b.modified_at).getTime() - new Date(a.modified_at).getTime()
    );

    result.push({
      version_number: versionNumber,
      status: versionItems[0].status,
      items: versionItems,
      latestModifiedAt: versionItems[0].modified_at,
      latestModifiedBy: versionItems[0].last_modified_by,
    });
  });

  return result;
}

export function beforeStateLabel(attrName?: string) {
  return "BEFORE" + (attrName ? ": " : "") + (attrName ? `${attrName}` : "");
}

export function afterStateLabel(attrName?: string) {
  return "AFTER" + (attrName ? ": " : "") + (attrName ? `${attrName}` : "");
}

export function imageUrlAndDisplayText(value: string | LinkValue): {
  url: string;
  displayText: string;
} {
  let imageUrl = "";
  let imageDisplayText = "";

  try {
    if (value && typeof value === "object") {
      const valueObj = value as LinkValue;
      imageUrl = valueObj.url || "";
      imageDisplayText = valueObj.display_text || "";
    } else if (typeof value === "string") {
      try {
        const valueObj = JSON.parse(value) as LinkValue;
        imageUrl = valueObj?.url || "";
        imageDisplayText = valueObj?.display_text || "";
      } catch {
        imageUrl = formatValueForDisplay(
          typeof value === "string" ? value : ""
        );
        imageDisplayText = "";
      }
    }
  } catch {
    imageUrl = formatValueForDisplay(typeof value === "string" ? value : "");
    imageDisplayText = "";
  }

  return { url: imageUrl, displayText: imageDisplayText };
}

function extractImageDataFromActivityValue(
  activityValue: ActivityHistoryValue
): {
  assetId?: string;
  caption?: string;
  dimensions?: string;
  signedUrl?: string;
  previewUrl?: string;
} {
  return {
    assetId:
      typeof activityValue.value === "string" ? activityValue.value : undefined,
    caption: activityValue.caption,
    dimensions: activityValue.image_dimensions,
    signedUrl: activityValue.signed_url,
    previewUrl: activityValue.preview_url,
  };
}

export function parseImageData(
  value: string | ActivityHistoryValue | null | undefined
): {
  assetId?: string;
  caption?: string;
  dimensions?: string;
  signedUrl?: string;
  previewUrl?: string;
} | null {
  if (!value) return null;

  try {
    if (typeof value === "string") {
      if (
        value.startsWith("http://") ||
        value.startsWith("https://") ||
        value.startsWith("/")
      ) {
        return { signedUrl: value };
      }

      try {
        const parsed = JSON.parse(value);
        if (
          parsed &&
          typeof parsed === "object" &&
          "attribute_id" in parsed &&
          "data_type" in parsed
        ) {
          return extractImageDataFromActivityValue(
            parsed as ActivityHistoryValue
          );
        }

        const data = parsed.value ? { ...parsed, ...parsed.value } : parsed;
        return {
          assetId: data.asset_id || data.id,
          caption: data.caption,
          dimensions: data.image_dimensions || data.dimensions,
          signedUrl: data.signed_url,
          previewUrl: data.preview_url,
        };
      } catch {
        return { assetId: value };
      }
    }

    if (typeof value === "object" && value !== null) {
      if ("attribute_id" in value && "data_type" in value) {
        return extractImageDataFromActivityValue(value as ActivityHistoryValue);
      }
    }
  } catch (error) {
    console.error("Error parsing image data:", error);
  }

  return null;
}

function getCollectionIdFromState(state: ActivityHistoryState): string | null {
  if (state?.value_row?.collection_id) return state.value_row.collection_id;
  const rows = state?.value_rows || [];
  if (rows.length > 0) {
    const set = new Set(
      rows
        .map((r: ValueRow) => r.collection_id)
        .filter((id): id is string => Boolean(id))
    );
    if (set.size === 1) return Array.from(set)[0] as string;
    if (set.size > 1) return "multiple";
  }
  return null;
}

function getGroupKey(item: ActivityHistoryItem): string | null {
  const hasGroupOrCollection =
    Boolean(item?.state_after?.groups) ||
    Boolean(item?.state_after?.collections) ||
    Boolean(item?.state_before?.groups) ||
    Boolean(item?.state_before?.collections);

  if (hasGroupOrCollection) {
    return "bulk";
  }
  if (item.target_type === "bulk-association") {
    return "bulk-association";
  }
  if (
    item.state_before?.list_price ||
    item.state_after?.list_price ||
    item.state_before?.list_prices ||
    item.state_after?.list_prices
  ) {
    return "pricing";
  }

  const beforeCid = getCollectionIdFromState(item.state_before);
  const afterCid = getCollectionIdFromState(item.state_after);
  const cid = beforeCid || afterCid;
  return cid ? `collection:${cid}` : null;
}

export function segmentActivitiesByGroupable(
  items: ActivityHistoryItem[]
): Array<{
  items: ActivityHistoryItem[];
  groupable: boolean;
  key: string | null;
}> {
  const segments: Array<{
    items: ActivityHistoryItem[];
    groupable: boolean;
    key: string | null;
  }> = [];
  let current: ActivityHistoryItem[] = [];
  let currentKey: string | null | undefined = undefined;
  let currentLastModifiedBy: string | undefined = undefined;

  items.forEach((activity) => {
    const key = getGroupKey(activity);
    if (currentKey === undefined) {
      currentKey = key;
      currentLastModifiedBy = activity.last_modified_by;
      current.push(activity);
      return;
    }

    const shouldGroup =
      key === currentKey &&
      (currentKey === null ||
        activity.last_modified_by === currentLastModifiedBy);
    if (shouldGroup) {
      current.push(activity);
    } else {
      segments.push({
        items: current,
        groupable: currentKey !== null,
        key: currentKey ?? null,
      });
      current = [activity];
      currentKey = key;
      currentLastModifiedBy = activity.last_modified_by;
    }
  });

  if (current.length) {
    segments.push({
      items: current,
      groupable: currentKey !== null,
      key: currentKey ?? null,
    });
  }
  return segments;
}

function isKeyedObject(
  obj: ActivityHistoryState | Record<string, ActivityHistoryState> | undefined
): boolean {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    return false;
  }
  const objRecord = obj as Record<
    string,
    ActivityHistoryState | ActivityHistoryState[keyof ActivityHistoryState]
  >;
  if (
    objRecord.attributes ||
    objRecord.values ||
    objRecord.value_rows ||
    objRecord.value_row
  ) {
    return false;
  }
  return true;
}

function extractStateProperties(
  state: ActivityHistoryState | Record<string, ActivityHistoryState> | undefined
): Partial<ActivityHistoryState> {
  if (!state || typeof state !== "object") {
    return {};
  }
  const stateRecord = state as Partial<ActivityHistoryState>;
  return {
    attributes: stateRecord.attributes,
    values: stateRecord.values,
    value_row: stateRecord.value_row,
    value_rows: stateRecord.value_rows,
    list_prices: stateRecord.list_prices,
    list_price: stateRecord.list_price,
    list_price_visibility: stateRecord.list_price_visibility,
    status: stateRecord.status,
    allow_custom_sku: stateRecord.allow_custom_sku,
    assets: stateRecord.assets,
    asset: stateRecord.asset,
  };
}

// Expand a single bulk activity (with top-level groups/collections) into
// a list of synthetic ActivityHistoryItem entries, one per group/collection.
// Handles dynamic formats where collections/groups can be keyed objects or direct states
export function expandBulkActivity(
  item: ActivityHistoryItem
): ActivityHistoryItem[] {
  const results: ActivityHistoryItem[] = [];

  const sb: ActivityHistoryState =
    item.state_before || ({} as ActivityHistoryState);
  const sa: ActivityHistoryState =
    item.state_after || ({} as ActivityHistoryState);

  // Handle groups
  if (sb.groups || sa.groups) {
    const beforeGroups = sb.groups as
      | ActivityHistoryState
      | Record<string, ActivityHistoryState>
      | undefined;
    const afterGroups = sa.groups as
      | ActivityHistoryState
      | Record<string, ActivityHistoryState>
      | undefined;

    if (isKeyedObject(beforeGroups) || isKeyedObject(afterGroups)) {
      const allGroupKeys = new Set([
        ...Object.keys(beforeGroups || {}),
        ...Object.keys(afterGroups || {}),
      ]);

      allGroupKeys.forEach((groupName) => {
        const beforeGroup =
          beforeGroups &&
          typeof beforeGroups === "object" &&
          !Array.isArray(beforeGroups) &&
          "attributes" in beforeGroups
            ? undefined
            : (
                beforeGroups as Record<string, ActivityHistoryState> | undefined
              )?.[groupName];
        const afterGroup =
          afterGroups &&
          typeof afterGroups === "object" &&
          !Array.isArray(afterGroups) &&
          "attributes" in afterGroups
            ? undefined
            : (
                afterGroups as Record<string, ActivityHistoryState> | undefined
              )?.[groupName];
        const sub: ActivityHistoryItem = {
          ...item,
          id: `${item.id}-group-${groupName}`,
          target_type: groupName,
          state_before: extractStateProperties(
            beforeGroup
          ) as ActivityHistoryState,
          state_after: extractStateProperties(
            afterGroup
          ) as ActivityHistoryState,
        };
        results.push(sub);
      });
    } else {
      const beforeGroup = beforeGroups || ({} as ActivityHistoryState);
      const afterGroup = afterGroups || ({} as ActivityHistoryState);
      const sub: ActivityHistoryItem = {
        ...item,
        id: `${item.id}-group`,
        target_type: item.target_type || "Group",
        state_before: extractStateProperties(
          beforeGroup
        ) as ActivityHistoryState,
        state_after: extractStateProperties(afterGroup) as ActivityHistoryState,
      };
      results.push(sub);
    }
  }

  // Handle collections
  if (sb.collections || sa.collections) {
    const beforeCollections = sb.collections as
      | ActivityHistoryState
      | Record<string, ActivityHistoryState>
      | undefined;
    const afterCollections = sa.collections as
      | ActivityHistoryState
      | Record<string, ActivityHistoryState>
      | undefined;

    if (isKeyedObject(beforeCollections) || isKeyedObject(afterCollections)) {
      const allCollectionKeys = new Set([
        ...Object.keys(beforeCollections || {}),
        ...Object.keys(afterCollections || {}),
      ]);

      allCollectionKeys.forEach((collectionName) => {
        const beforeCol =
          beforeCollections &&
          typeof beforeCollections === "object" &&
          !Array.isArray(beforeCollections) &&
          "attributes" in beforeCollections
            ? undefined
            : (
                beforeCollections as
                  | Record<string, ActivityHistoryState>
                  | undefined
              )?.[collectionName];
        const afterCol =
          afterCollections &&
          typeof afterCollections === "object" &&
          !Array.isArray(afterCollections) &&
          "attributes" in afterCollections
            ? undefined
            : (
                afterCollections as
                  | Record<string, ActivityHistoryState>
                  | undefined
              )?.[collectionName];
        const sub: ActivityHistoryItem = {
          ...item,
          id: `${item.id}-collection-${collectionName}`,
          target_type: collectionName,
          state_before: extractStateProperties(
            beforeCol
          ) as ActivityHistoryState,
          state_after: extractStateProperties(afterCol) as ActivityHistoryState,
        };
        results.push(sub);
      });
    } else {
      const beforeCol = beforeCollections || ({} as ActivityHistoryState);
      const afterCol = afterCollections || ({} as ActivityHistoryState);
      const sub: ActivityHistoryItem = {
        ...item,
        id: `${item.id}-collection`,
        target_type: item.target_type || "Collection",
        state_before: extractStateProperties(beforeCol) as ActivityHistoryState,
        state_after: extractStateProperties(afterCol) as ActivityHistoryState,
      };
      results.push(sub);
    }
  }

  if (sb.assets || sa.assets) {
    const beforeAssets = sb.assets as Assets[] | undefined;
    const afterAssets = sa.assets as Assets[] | undefined;

    if (
      (beforeAssets &&
        Array.isArray(beforeAssets) &&
        beforeAssets.length > 0) ||
      (afterAssets && Array.isArray(afterAssets) && afterAssets.length > 0)
    ) {
      results.push({
        ...item,
        id: `${item.id}-assets`,
        state_before: {
          ...sb,
          assets: beforeAssets,
        },
        state_after: {
          ...sa,
          assets: afterAssets,
        },
      });
    }
  }

  return results;
}

/**
 * Normalizes HTML by adding line breaks after block-level elements for better diffing.
 * This helps preserve complete HTML tags during line-by-line diffing.
 */
function normalizeHtmlForDiffing(html: string): string {
  if (!html) return "";

  return html
    .replace(
      /(<\/(?:p|div|h[1-6]|ol|ul|li|blockquote|pre|table|tr|td|th|section|article|header|footer|nav|aside|br)>)/gi,
      "$1\n"
    )
    .replace(
      /(<(?:p|div|h[1-6]|ol|ul|li|blockquote|pre|table|tr|td|th|section|article|header|footer|nav|aside|br)[^>]*\/?>)/gi,
      "\n$1"
    )
    .replace(/\n\n+/g, "\n")
    .trim();
}

/**
 * Renders HTML content with diff highlighting.
 * Used for html, multiline, and multiline_entry input types.
 */
export function renderHtmlWithDiff(
  before: string,
  after: string,
  isBefore: boolean
): React.ReactNode {
  if (!before && !after) {
    return EMPTY_VALUE;
  }

  const value = isBefore ? before : after;

  if (!value) {
    return EMPTY_VALUE;
  }

  const normalizedBefore = normalizeHtmlForDiffing(before || "");
  const normalizedAfter = normalizeHtmlForDiffing(after || "");

  const diffParts = diffLines(normalizedBefore, normalizedAfter);
  const hasDiff = diffParts.some((part) => part.added || part.removed);

  if (!hasDiff) {
    return React.createElement("div", {
      dangerouslySetInnerHTML: {
        __html: `<div class="ql-editor">${value}</div>`,
      },
      style: {
        display: "inline-block",
      },
    });
  }

  const filteredParts = diffParts.filter((part) =>
    isBefore ? !part.added : !part.removed
  );

  return React.createElement(
    "div",
    {
      className: "ql-editor",
      style: {
        display: "block",
        whiteSpace: "pre-wrap",
        width: "fit-content",
      },
    },
    filteredParts.map((part, index) =>
      React.createElement(DiffText, {
        key: index,
        added: !isBefore && Boolean(part.added),
        removed: isBefore && Boolean(part.removed),
        dangerouslySetInnerHTML: {
          __html: part.value?.replace(/\n/g, "") || "&nbsp;",
        },
      })
    )
  );
}

export function isMultiValue(
  inputType: string,
  is_multiple_choice: boolean
): boolean {
  return (
    inputType === "multi_select" ||
    ((inputType === "product_reference" ||
      inputType === "object_reference" ||
      inputType === "user" ||
      inputType === "document") &&
      is_multiple_choice)
  );
}
